import { useCallback, useRef, type ComponentProps } from 'react'
import { Markdown, Popover } from '@highlight/ui'
import { type ReminderItem } from '../../hooks/use-reminders'
import { AiCardPrompt } from '../ai-card-prompt'
import { TodoPopover } from '../todo-popover'
import { useAskAboutReminder } from './hooks/use-ask-about-reminder'
import { useReminderImageAttach } from './utils/image-attachments'
import { ReminderAttachments } from './nested/reminder-attachments'
import { ReminderDetailActions } from './nested/reminder-detail-actions'
import { ReminderTitle } from './nested/reminder-title'
import { ReminderNotes } from './nested/reminder-notes'

const POPOVER_TRIGGER = 'todo-popover'

interface ReminderPopoverProps extends Pick<
  ComponentProps<typeof TodoPopover>,
  'open' | 'onOpenChange' | 'anchor' | 'previewing' | 'swapping'
> {
  item: ReminderItem
  /** After a change the row wants to flash as saved. */
  onSaving: () => void
  onClose: () => void
}

/**
 * A To-dos reminder opened from its row: Highlight's description on the left
 * over a prompt for chatting about it, and the editable title, the user's notes
 * and the due date and pin on the right. Without a description there's no card,
 * and a chat button joins the due date and pin.
 */
export function ReminderPopover({ item, onSaving, onClose, ...popover }: ReminderPopoverProps) {
  const askAboutReminder = useAskAboutReminder()
  const attach = useReminderImageAttach(item.id, onSaving)
  const resolved = item.resolved === true
  const autoResolvedEvidence = resolved ? item.autoResolved?.evidence : undefined
  const hasAside = !!item.description || !!autoResolvedEvidence
  // The arrow keys cross between the title and the notes the way they cross lines within either.
  const titleRef = useRef<HTMLTextAreaElement>(null)
  const notesRef = useRef<HTMLTextAreaElement>(null)
  const focusNotes = useCallback(() => {
    const field = notesRef.current
    field?.focus()
    field?.setSelectionRange(0, 0)
  }, [])
  const focusTitle = useCallback(() => {
    const field = titleRef.current
    field?.focus()
    field?.setSelectionRange(field.value.length, field.value.length)
  }, [])

  return (
    <TodoPopover
      {...popover}
      aside={
        hasAside && (
          <>
            {item.description && <Markdown size="sm">{item.description}</Markdown>}
            {autoResolvedEvidence && (
              <p className="text-foreground-secondary type-ui-xs">Checked off by Highlight · {autoResolvedEvidence}</p>
            )}
          </>
        )
      }
      asidePrompt={
        hasAside && (
          <AiCardPrompt
            placeholder="Ask about reminder…"
            onSubmit={(prompt) => {
              onClose()
              void askAboutReminder(item, prompt, POPOVER_TRIGGER)
            }}
          />
        )
      }
      footer={
        <ReminderDetailActions
          item={item}
          trigger={POPOVER_TRIGGER}
          onSaving={onSaving}
          showsChat={!hasAside}
          attach={attach}
          onLeave={onClose}
        />
      }
    >
      <input
        ref={attach.inputRef}
        type="file"
        className="hidden"
        accept="image/*"
        multiple
        onChange={attach.onInputChange}
      />
      <Popover.Title className="sr-only">{item.title}</Popover.Title>
      <ReminderTitle
        item={item}
        trigger={POPOVER_TRIGGER}
        onSaving={onSaving}
        fieldRef={titleRef}
        onArrowDownOut={focusNotes}
      />
      <ReminderAttachments item={item} onSaving={onSaving} pending={attach.pending} className="my-1" />
      <ReminderNotes item={item} onSaving={onSaving} fieldRef={notesRef} onArrowUpOut={focusTitle} />
    </TodoPopover>
  )
}
